import React, { useState } from 'react';
import AvItemWithLogoGroup, { IconSize } from '@components/AvItemWithLogoGroup';
import { IconVariant } from '@components/ItemWithLogo';
import AvTable from '@components/Table/AvTable';
import { TextInput } from '@fabric-ui/components';
import { ActionButtonV2, HeadCellType } from '@fabric-ui/components/Table';
import { External, Search } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { Entities } from '@views/Threat/Entities';
import { format } from 'date-fns';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import { getAlertTable, getIncidentTable } from './queries';
import { FilterContext, TableDataType } from './types';
import { getTableTypeTitle } from './utils';

interface InteractiveTableProps {
  filterContext: FilterContext;
  tableType: TableDataType;
}

const CREATED_FORMATED = 'MMM dd, yyyy';

const getTableConfig = (tableType: TableDataType, filterContext: FilterContext, page: number, pageSize: number) => {
  const baseHeadCells: HeadCellType[] = [{ id: `key`, label: 'Id', isKey: true, hidden: true }];

  if (tableType === TableDataType.INCIDENT) {
    const incidentHeadCells: HeadCellType[] = [
      ...baseHeadCells,
      { id: `risk_mass`, label: 'RISK MASS', style: { fontWeight: 600 } },
      { id: `incident.title`, label: 'INCIDENT' },
      {
        id: `incident.first_seen`,
        label: 'CREATED',
        formatter: v => (v ? format(new Date(v), CREATED_FORMATED) : ''),
      },
      {
        id: `incident.source_names`,
        label: 'SOURCE',
        formatter: sources => (
          <AvItemWithLogoGroup
            variant={IconVariant.sourcesMapByName}
            items={sources || []}
            size={IconSize.collapsed}
            limit={1}
            inputVariantSpace={8}
          />
        ),
      },
    ];
    const headCells = incidentHeadCells;
    const query = getIncidentTable(filterContext, page, pageSize);
    return { headCells, query };
  }

  const alertHeadCells: HeadCellType[] = [
    ...baseHeadCells,
    { id: `alert.title`, label: 'ALERT' },
    { id: `alert.severity_score`, label: 'SCORE', style: { fontWeight: 600 } },
    { id: `total_incidents`, label: 'INCIDENTS' },
    { id: `mitre_attack.technique`, label: 'MITRE TECHNIQUE' },
    {
      id: `alert.user_keys`,
      label: 'ENTITIES',
      formatter: (v, row) =>
        v ? <Entities users={row['alert.user_keys']?.length || 0} assets={row['alert.asset_keys']?.length || 0} /> : '',
    },
    { id: `alert.first_seen`, label: 'CREATED', formatter: v => (v ? format(new Date(v), CREATED_FORMATED) : '') },
  ];
  const headCells = alertHeadCells;
  const query = getAlertTable(filterContext, page, pageSize);
  return { headCells, query };
};

const InteractiveTable = (props: InteractiveTableProps) => {
  const { filterContext, tableType } = props;

  const PAGE_SIZE = 100;
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const tableFilterContext = React.useMemo(() => {
    if (!searchTerm) {
      return filterContext;
    }
    return {
      ...filterContext,
      search: {
        field: `${tableType.toLowerCase()}.title`,
        term: searchTerm,
      },
    };
  }, [filterContext, searchTerm, tableType]);

  const { headCells, query } = React.useMemo(
    () => getTableConfig(tableType, tableFilterContext, page, PAGE_SIZE),
    [tableFilterContext, page]
  );

  const { data, isLoading, totalRowCount, isRefetching } = useQueryObject({
    queryObject: query,
    debounceQueries: true,
    totalRowCount: true,
  });

  const gotoEntityRowInlineAction = row =>
    ActionButtonV2({
      label: `Open ${getTableTypeTitle(tableType).slice(0, -1)} in a new tab`,
      icon: <External />,
      onClick: () => {
        window.open(`${getTableTypeTitle(tableType).toLowerCase()}/${row.key}`, '_blank');
      },
    });

  return (
    <Box sx={{ ...flex.col, gap: 2, overflow: 'auto' }}>
      <TextInput
        size="small"
        helperText="Search"
        icon={<Search style={{ ...iconSize(16) }} />}
        value={searchTerm}
        onChange={setSearchTerm}
        sx={{
          borderRadius: '10px',
          width: '300px',
          '.MuiOutlinedInput-root': {
            height: '32px',
            gap: '8px',
            padding: '12px 8px 12px 16px',
          },
        }}
      />
      <AvTable
        storageKey="InteractiveList"
        rows={data}
        headCells={headCells}
        loading={isLoading || isRefetching}
        allowEdit={false}
        disableRowClickNavigation={() => true}
        hasHeaderFilters={false}
        disableSelectAll
        inlineActions={[gotoEntityRowInlineAction]}
        rowLimit={PAGE_SIZE}
        size="xSmall"
        pagination
        paginationProps={{ onPageChange: setPage, pageNum: page, totalRows: totalRowCount }}
      />
    </Box>
  );
};

export default InteractiveTable;
